import SendIcon from "@mui/icons-material/Send";
import { Button } from "@mui/material";
import { LoadingButton } from "@mui/lab";
import { useRecordContext, useRefresh } from "react-admin";
import { DateTime } from "luxon";
import { useState } from "react";
import { reportError } from "../backoffice.utils";
import { serverAPI } from "../api/server.api";
import { LocalizedError } from "../utils/LocalizedError";
import { draftDunningNotice, postponeDunningProcess } from "../api/backend.api";
import { Student } from "../providers/studentsProvider";
import { useMutation } from "react-query";
import { useDialog } from "../hooks/useDialog";
import { SendDunningNoticeDialog, SendDunningNoticeFormValues } from "../dialogs/SendDunningNoticeDialog";
import type { EmailDraft } from "../generated/backendClient";
import { EventPropagationStopper } from "../misc/EventPropagationStopper";

export const SendFirstDunningNoticeButton = ({ variant = "rowButton" }: { variant?: "rowButton" | "dialogButton" }) => {
  const student = useRecordContext<Student>();
  const refresh = useRefresh();
  const { dialogProps, openDialog, closeDialog } = useDialog();
  const [draftingEmail, setDraftingEmail] = useState(false);
  const [dunningProcessId, setDunningProcessId] = useState<string>();
  const [draft, setDraft] = useState<EmailDraft | undefined>();

  const draftEmailAndOpenDialog = async (event: React.MouseEvent) => {
    event.preventDefault();
    event.stopPropagation();
    setDraftingEmail(true);
    try {
      const { dunningProcess, draft } = await draftDunningNotice(student, "first_dunning_notice");
      setDunningProcessId(dunningProcess.id);
      setDraft(draft);
      openDialog();
    } catch (error) {
      if (error instanceof LocalizedError) {
        showErrorToast(error.message);
      } else {
        reportError("Failed to draft first dunning notice", error);
        showErrorToast(
          "Oje, es ist ein Fehler beim Vorbereiten der 1. Mahnung aufgetreten. Bitte wende Dich an die Technik.",
        );
      }
    } finally {
      setDraftingEmail(false);
    }
  };

  const { mutateAsync: sendFirstDunningNotice } = useMutation(
    async ({ emailSubject, emailText, postponementDate }: SendDunningNoticeFormValues) => {
      if (!dunningProcessId) {
        throw new Error("dunningProcessId not set");
      }
      await serverAPI.sendDunningNotice({
        student,
        dunningProcessId,
        noticeType: "first_dunning_notice",
        emailSubject,
        emailText,
      });
      await postponeDunningProcess(dunningProcessId, student.id, postponementDate);
    },
    {
      onSuccess: () => {
        refresh();
        showSuccessToast("1. Mahnung erfolgreich versandt.");
        closeDialog();
      },
      onError: (error) => {
        reportError("Failed to send first dunning notice", error);
        showErrorToast("Fehler beim Versenden der 1. Mahnung");
      },
    },
  );

  return (
    <EventPropagationStopper>
      {variant === "rowButton" ? (
        <LoadingButton
          variant="outlined"
          loading={draftingEmail}
          startIcon={<SendIcon />}
          disabled={!student}
          onClick={draftEmailAndOpenDialog}
        >
          {"1.\u00A0Mahnung"}
        </LoadingButton>
      ) : (
        <Button variant="text" disableRipple sx={{ height: "20px" }} onClick={draftEmailAndOpenDialog}>
          1. Mahnung versenden
        </Button>
      )}
      <SendDunningNoticeDialog
        title="1. Mahnung"
        {...dialogProps}
        initialFormValues={{
          emailSubject: draft?.subject ?? "",
          emailText: draft?.text ?? "",
          postponementDate: DateTime.now().plus({ days: 14 }),
        }}
        onSubmit={sendFirstDunningNotice}
      />
    </EventPropagationStopper>
  );
};
